import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { AlertTriangle } from 'lucide-react'

type PendingAction = (() => void) | null

interface NavigationGuardContextValue {
  setBlock: (shouldBlock: boolean) => void
  requestNavigation: (action: () => void) => void
}

const NavigationGuardContext = createContext<NavigationGuardContextValue | null>(null)

export function NavigationGuardProvider({ children }: { children: React.ReactNode }) {
  const [shouldBlock, setShouldBlock] = useState(false)
  const [pendingAction, setPendingAction] = useState<PendingAction>(null)
  const shouldBlockRef = useRef(false)

  useEffect(() => {
    shouldBlockRef.current = shouldBlock
  }, [shouldBlock])

  const setBlock = useCallback((value: boolean) => {
    setShouldBlock(value)
  }, [])

  const requestNavigation = useCallback((action: () => void) => {
    if (shouldBlockRef.current) {
      setPendingAction(() => action)
    } else {
      action()
    }
  }, [])

  useEffect(() => {
    const handler = (event: BeforeUnloadEvent) => {
      if (shouldBlockRef.current) {
        event.preventDefault()
        event.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [])

  const confirmDiscard = () => {
    setShouldBlock(false)
    const action = pendingAction
    setPendingAction(null)
    if (action) action()
  }

  const cancelDiscard = () => {
    setPendingAction(null)
  }

  const value = useMemo<NavigationGuardContextValue>(
    () => ({ setBlock, requestNavigation }),
    [setBlock, requestNavigation]
  )

  return (
    <NavigationGuardContext.Provider value={value}>
      {children}
      {pendingAction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-start space-x-3 mb-4">
              <div className="flex-shrink-0">
                <AlertTriangle className="h-6 w-6 text-orange-500" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Modifications non sauvegardees</h3>
                <p className="text-sm text-gray-600 mt-2">
                  Vous avez des modifications de presences qui n'ont pas ete enregistrees. Si vous quittez maintenant, elles seront perdues.
                </p>
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelDiscard}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Rester sur la page
              </button>
              <button
                onClick={confirmDiscard}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Abandonner les modifications
              </button>
            </div>
          </div>
        </div>
      )}
    </NavigationGuardContext.Provider>
  )
}

export function useNavigationGuard(): NavigationGuardContextValue {
  const context = useContext(NavigationGuardContext)
  if (!context) {
    throw new Error('useNavigationGuard must be used within a NavigationGuardProvider')
  }
  return context
}

export function useBlockNavigation(shouldBlock: boolean) {
  const { setBlock } = useNavigationGuard()
  useEffect(() => {
    setBlock(shouldBlock)
    return () => setBlock(false)
  }, [shouldBlock, setBlock])
}
